// @flow

import $ from 'jquery'
import log from 'loglevel'
import { trackError } from '../../lib/analytics/fpti'
import { postMessageUp, isIframe } from 'iframe-support'

// used for grabbing CSRF token dynamically (needed for portable)
// works because calculator doesn't do any POSTs
import store from '../../transfer/stores/configureStore'

const errorHandlers = {
  '401': redirectToProperUrl,
  '302': redirectToProperUrl,
  default: defaultErrorHandler,
}

/**
 * Convert the text-based error response into a JSON response
 */
function defaultErrorHandler(jqxhr) {
  let parsedResponse
  let error
  try {
    const response = jqxhr.responseText
    parsedResponse = response && JSON.parse(response)
    error = typeof parsedResponse === 'object' && parsedResponse.error
    if (typeof error === 'object') {
      error.debugId = getDebugId(jqxhr)
      error.category = parsedResponse.error.category
      error.statusCode = jqxhr.status
    }
  } catch (e) {
    log.warn(`Error in xhr defaultErrorHandler: ${JSON.stringify(e.message)}`)
  }
  return Promise.reject(error || jqxhr)
}

/**
 * Util to save debug ids to the DOM. Used for quick debugging in regression testing reports.
 */
function saveDebugIdToDOM(url, method, debugId, status, failure) {
  // failure is param, because unfortunately status is not enough
  let $debugEl = $('#debug-ids')
  if (!$debugEl.length) {
    $debugEl = $('body')
      .prepend('<script id="debug-ids">')
      .find('#debug-ids')
  }
  let debugData = JSON.parse($debugEl.attr('data-debug-ids') || '{}')
  const newData = {
    [`${url.replace(/\?.*/g, '')}-${debugId}`]: {
      debugId,
      method,
      status,
      failure,
    },
  }
  debugData = { ...debugData, ...newData }
  $debugEl.attr('data-debug-ids', JSON.stringify(debugData))
}

/**
 * Util that will convert jQuery promises to native promises. Keeps all args
 * passed from jquery resolve/reject as single object passed to native promise.
 * jQuery supports multiple args to resolve/reject, but native promises only allow
 * for a single arg.
 */
function toNativePromise($promise, request: Object): Promise<Object> {
  return new Promise(function(resolve, reject) {
    // eslint-disable-next-line
    $promise.then(
      (data = {}, textStatus, jqXHR) =>
        resolve({ data, textStatus, jqXHR, request }),
      (jqXHR, textStatus, errorThrown) =>
        reject({ jqXHR, textStatus, errorThrown, request })
    )
  })
}

/**
 * Simple wrapper around $.ajax which returns functions for each unique http method
 */
function ajax(
  type,
  overrides: ?Object = {}
): (url: string, data: ?Object) => Promise<Object> {
  return function(url: string, data: ?Object = {}): Promise<Object> {
    let useQueryParams = false
    let useCSRF = false
    let csrfToken
    if (type === 'GET' || type === 'DELETE') {
      useQueryParams = true
    }
    if (type === 'POST' || type === 'PUT' || type === 'PATCH') {
      useCSRF = true
      csrfToken = store.getState().server.csrfToken
    }
    data = {
      ...data,
      attemptId: store.getState().transaction.attemptId, // for tracking purposes
    }
    const request = {
      type,
      url: useQueryParams ? `${url}?${$.param(data)}` : url,
      data: useQueryParams ? undefined : data,
      headers: useCSRF ? { 'X-CSRF-Token': csrfToken } : undefined,
      dataType: 'json',
      ignoreErrors: true, // avoids triggering the 500 page when a failure occurs (see: ajaxError.js)
      ...overrides,
    }
    // If we are sending JSON, the body of the request must be stringified by us.
    if (/application\/json/.test(request.contentType)) {
      request.data = request.data && JSON.stringify(data)
    }
    const nativePromise = toNativePromise($.ajax(request), request)
    return nativePromise.then(onSuccess, onError)
  }
}

/**
 * handle the error scenario
 */
function onError(response: Object = {}) {
  const { jqXHR, request } = response
  if (!store.getState().server.isProduction) {
    const { url, type: method } = request
    const debugId = getDebugId(jqXHR) || 'N/A'
    saveDebugIdToDOM(url, method, debugId, jqXHR.status, true)
  }
  if (jqXHR === undefined) {
    return
  }
  const errorHandler = errorHandlers[jqXHR.status] || errorHandlers.default
  return errorHandler(jqXHR)
}

/**
 * Read the error message off the 200 response and throw it.
 * If called within a Promise it will end up in the .catch() function.
 */
function onSuccess(response: Object = {}) {
  const { jqXHR, data } = response
  const {
    error: { code: errorCode = '', message: errorMessage = '' } = {},
  } = data
  if (errorCode === 'authRequired') {
    const { data: { location } = {} } = data
    // Redirect even when within an iframe, if root window is paypal.com; otherwise,
    // csp headers kick in and prevent the redirection.
    if (isIframe()) {
      postMessageUp({
        statusMessage: 'authRequired',
        location,
      })
      window.parent.location = location
    } else {
      window.location = location
    }

    return Promise.reject({
      ...data.error,
      statusCode: data.statusCode,
      debugId: getDebugId(jqXHR),
    })
  }

  if (errorMessage) {
    const message = JSON.stringify(data.error)
    const code = data.statusCode || 500
    trackError({ message, code, debugId: getDebugId(jqXHR) })
    return Promise.reject({
      ...data.error,
      statusCode: code,
      debugId: getDebugId(jqXHR),
    })
  }

  if (!store.getState().server.isProduction) {
    const { url, type: method } = response.request
    const debugId = getDebugId(jqXHR) || 'N/A'
    saveDebugIdToDOM(url, method, debugId, jqXHR.status, !!errorMessage)
  }

  // pass statusCode & debugId for successful transactions as well
  data.statusCode = data.statusCode || jqXHR.status
  data.debugId = data.debugId || getDebugId(jqXHR)

  return data
}

/**
 * Handle cases where the server sends a redirection message
 * @param {Object} jqxhr the jquery XHR object
 * @TODO consider combining with global AJAX error handler in globa.js
 */
function redirectToProperUrl(jqxhr) {
  const redirectUrl = getRedirectLocation(jqxhr)
  if (redirectUrl) {
    // force a full page reload
    window.location.assign(redirectUrl)
  }
  return Promise.reject(jqxhr)
}

/**
 * Handy function to get the redirect location from the error object
 * @param {Object} jqxhr the error object from which to derive the redirect url
 * @return {String} the url to redirect to
 */
function getRedirectLocation(jqxhr) {
  const location =
    jqxhr.getResponseHeader && jqxhr.getResponseHeader('location')
  if (location) {
    return location
  }
  if (jqxhr.responseText) {
    const responseParts = jqxhr.responseText.split(' ')
    return responseParts.find(part => part.startsWith('http'))
  }
}

/**
 * Handy function to get the debug id
 * @param {Object} jqxhr
 * @return {String} the debug id (aka correlation id)
 */
function getDebugId(jqxhr) {
  return (
    jqxhr &&
    jqxhr.getResponseHeader &&
    jqxhr.getResponseHeader('paypal-debug-id')
  )
}

// all exports go at the bottom of the file here to make it easier to find what's exported
export const fetch = ajax('GET')
export const post = ajax('POST')
export const postJson = ajax('POST', {
  contentType: 'application/json; charset=utf-8',
})
export const put = ajax('PUT')
export const deleteAjax = ajax('DELETE')
export const patch = ajax('PATCH')
export const patchJson = ajax('PATCH', {
  contentType: 'application/json; charset=utf-8',
})
